import dayjs from 'dayjs';
import { getWeekdays } from '@product-framework/index';
import { SnackbarApiErrorMessage } from '@ui-framework/index';
import { TIME_LIKE_RE, DEFAULT_CUTOFF_TIME } from './constants';

const fetchCutOffDayOptions = async ({ params, dayOfWeek, maxDaysBefore }) => {
  const url = `${params?.getApiUrl()}/sxcl/v1/availability/cutoff-day-options/${dayOfWeek}?maxDaysBefore=${maxDaysBefore}`;
  const response = await params?.get(url);
  if (response?.ok) {
    return response.data;
  }
  params?.snackbar.showMessage({
    type: 'error',
    data: <SnackbarApiErrorMessage errors={response?.errors} />,
  });
  return null;
};

/** Dropdown label: hour only in 24h notation (minutes always :00). */
function formatCutoffHourOption24h(hour) {
  const h = Number(hour);
  if (!Number.isInteger(h) || h < 0 || h > 23) return '12:00';
  return `${String(h).padStart(2, '0')}:00`;
}

function effectiveDateIsFuture(effectiveDateStr) {
  const ed = dayjs(effectiveDateStr).startOf('day');
  const today = dayjs().startOf('day');
  return ed.isAfter(today, 'day');
}

function formatCutoffTimeDisplay(timeStr) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(timeStr);
  if (!m) return timeStr;
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  h %= 12;
  if (h === 0) h = 12;
  return `${h}:${min} ${ampm}`;
}

/**
 * When no cutoff is stored, Tue–Sun default to previous calendar day at noon;
 * Monday defaults to the previous Friday at noon (3 calendar days earlier).
 * @param {string} wk key from WEEKDAY_KEYS
 * @returns {{ daysBefore: number, time: string }}
 */
export function getDefaultCutoffForWeekday(wk) {
  if (wk === 'MONDAY') {
    return { daysBefore: 3, time: DEFAULT_CUTOFF_TIME };
  }
  return { daysBefore: 1, time: DEFAULT_CUTOFF_TIME };
}

/**
 * Parse stored `HH:00` / legacy `HH:mm` into hour 0–23.
 * @param {string} [timeStr]
 * @returns {number}
 */
function parseCutoffHourFromStoredTime(timeStr) {
  const s = typeof timeStr === 'string' ? timeStr.trim() : '';
  const m = /^([01]?\d|2[0-3])(?::([0-5]\d))?$/.exec(s);
  if (!m) return 12;
  const h = Number(m[1]);
  if (!Number.isInteger(h) || h < 0 || h > 23) return 12;
  return h;
}

function normalizeCutoffValue(raw, jobWeekdayKey) {
  const fallback = getDefaultCutoffForWeekday(jobWeekdayKey);
  if (!raw || typeof raw !== 'object') return { ...fallback };
  let daysBefore = Number(raw.daysBefore);
  if (!Number.isInteger(daysBefore) || daysBefore < 0 || daysBefore > 6) {
    daysBefore = fallback.daysBefore;
  }
  let time = typeof raw.time === 'string' ? raw.time.trim() : fallback.time;
  if (!TIME_LIKE_RE.test(time)) {
    time = fallback.time;
  } else {
    const hour = parseCutoffHourFromStoredTime(time);
    time = `${String(hour).padStart(2, '0')}:00`;
  }
  return { daysBefore, time };
}

/** Calendar weekday name for the cutoff day given job weekday and offset. */
function getCalendarDayLabelForJobWeekday(
  jobWeekdayKey,
  daysBefore,
  formatMessage,
) {
  const weekdays = getWeekdays(formatMessage);
  const idx = weekdays.findIndex(w => w.value === jobWeekdayKey);
  if (idx < 0) return '';
  const targetIdx = (idx - daysBefore + 49) % 7;
  return weekdays[targetIdx].longLabel;
}

function formatCutoffSummary(jobWeekdayKey, cutoff, formatMessage) {
  const c = normalizeCutoffValue(cutoff, jobWeekdayKey);
  const dayName = getCalendarDayLabelForJobWeekday(
    jobWeekdayKey,
    c.daysBefore,
    formatMessage,
  );
  return `${dayName} ${formatCutoffTimeDisplay(c.time)}`;
}

export default {
  effectiveDateIsFuture,
  formatCutoffSummary,
  fetchCutOffDayOptions,
  formatCutoffHourOption24h,
  formatCutoffTimeDisplay,
  getDefaultCutoffForWeekday,
};
